import React from "react";
import { docSpaces, formTemplates } from "../../../../data/workspaceData";
import { SectionHeader } from "../../common/WorkspaceChrome";

const knowledgeSections = {
  Overview: {
    eyebrow: "Knowledge",
    title: "Documentation control center",
    text: "A single place for workspace documentation, SOPs, templates, policies, client documents, project documentation, training material, wiki pages, and version history.",
    items: [
      "Keep operational documents connected to projects, clients, tasks, tickets, training batches, and people records.",
      "Use this section for living documentation, not one-time exports. Downloads are only for files users need to take outside the workspace.",
      "Every document should eventually support ownership, version history, approvals, comments, and read receipts.",
    ],
  },
  Documents: {
    eyebrow: "Documents",
    title: "All workspace documents",
    text: "General documents, notes, requirements, references, meeting summaries, and shared files that do not belong only to one specialized folder.",
    items: ["Create and organize documents", "Attach docs to tasks, tickets, projects, clients, or employees", "Track owner, last update, visibility, and approval status"],
  },
  SOPs: {
    eyebrow: "SOPs",
    title: "Standard operating procedures",
    text: "Repeatable processes for delivery, support, HR, finance, onboarding, training, and client communication.",
    items: ["Step-by-step process docs", "Role-specific responsibility notes", "Approval and audit-friendly operating instructions"],
  },
  Templates: {
    eyebrow: "Templates",
    title: "Reusable workspace templates",
    text: "Reusable formats for proposals, onboarding checklists, tickets, reports, meeting notes, project plans, and training material.",
    items: ["Project templates", "Ticket and task templates", "Client onboarding and training templates"],
  },
  Policies: {
    eyebrow: "Policies",
    title: "Workspace policies",
    text: "Company rules, HR policies, security guidelines, billing rules, training rules, and client-facing operating policies.",
    items: ["Attendance and leave policy", "Security and access policy", "Client service and payment policy"],
  },
  "Client Docs": {
    eyebrow: "Client Docs",
    title: "Client-specific documentation",
    text: "Requirements, contracts, notes, communication records, deliverables, approvals, and client-visible documentation.",
    items: ["Client requirement docs", "Approval notes and shared deliverables", "Contract and onboarding files"],
  },
  "Project Docs": {
    eyebrow: "Project Docs",
    title: "Project documentation",
    text: "Scope, milestones, timelines, technical decisions, assets, releases, handover notes, and delivery documentation.",
    items: ["Scope and milestone docs", "Design and development references", "Release and handover notes"],
  },
  "Training Docs": {
    eyebrow: "Training Docs",
    title: "Training and learning material",
    text: "Programs, batches, sessions, assignments, certificates, assessments, student resources, and instructor references.",
    items: ["Program and batch material", "Assignments and assessments", "Certificate and placement documentation"],
  },
  Wiki: {
    eyebrow: "Wiki",
    title: "Internal workspace wiki",
    text: "A searchable shared knowledge base for teams to understand systems, decisions, people, workflows, and common answers.",
    items: ["Team knowledge pages", "Decision history", "Frequently used references"],
  },
  Versions: {
    eyebrow: "Versions",
    title: "Version history and document control",
    text: "Track edits, approval changes, published versions, archived copies, and document ownership over time.",
    items: ["Document version timeline", "Approval and publication history", "Restore or compare previous versions"],
  },
};

export function DocsView({ activeTab = "Overview" }) {
  const section = knowledgeSections[activeTab] || knowledgeSections.Overview;

  return (
    <>
      <section className="module-header-card">
        <SectionHeader
          eyebrow={section.eyebrow}
          title={section.title}
          text={section.text}
        />
        <div className="context-doc-points">
          {section.items.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>
      </section>
      <section className="split-layout">
        <div className="panel">
        <SectionHeader
          eyebrow="Documentation Spaces"
          title="Organized knowledge areas"
          text="Each space should become searchable, permission-aware, versioned, and connected to real workspace records."
        />
        <div className="doc-form-grid">
          {docSpaces.map((space) => (
            <article key={space.name}>
              <strong>{space.name}</strong>
              <span>{space.docs} docs</span>
              <p>{space.linked}</p>
              <small>{space.status}</small>
            </article>
          ))}
        </div>
      </div>
      <div className="panel">
        <SectionHeader
          eyebrow="Structured Docs"
          title="Templates, forms, and intake documents"
          text="Structured documentation can create leads, clients, projects, tasks, tickets, employees, training requests, and onboarding checklists."
        />
        <div className="doc-form-grid">
          {formTemplates.map((form) => (
            <article key={form.name}>
              <strong>{form.name}</strong>
              <span>{form.fields} fields</span>
              <p>{form.convertsTo}</p>
              <small>Form</small>
            </article>
          ))}
        </div>
      </div>
    </section>
    </>
  );
}
